'use client';

import type { DealerVehicle } from '@core/Entities/Vehicle/DealerVehicle.entity';
import type { Vehicle } from '@core/Entities/Vehicle/Vehicle.entity';
import type { Status as VehicleStatus } from '@core/Entities/Vehicle/VehicleLiterals.entity';
import { getPromotions } from '@core/Utils/Promotions/getPromotions';
import { BadgeItem } from '@gds/Badge/BadgeItem';
import { PromotionBadges } from '@shared-components/PromotionBadge/PromotionBadge';

import styles from './PdpBadges.module.css';

interface Props {
  strings: Record<string, string>;
  vehicle: Vehicle | DealerVehicle;
  isLeasing?: boolean;
}

const PdpBadges = ({ strings, vehicle, isLeasing }: Props) => {
  const isWholesaleVehicle = 'status' in vehicle;
  const isWholesaleSold = isWholesaleVehicle && vehicle.status === 'sold';
  const statusString = getStatusString(strings, vehicle?.details.status);
  const { hasPromotions, promotions } = isWholesaleVehicle
    ? { hasPromotions: false, promotions: [] }
    : getPromotions(vehicle as Vehicle);

  return hasPromotions ? (
    <aside className={styles.badges}>
      <PromotionBadges promotions={promotions} page="pdp" />
    </aside>
  ) : (
    <aside className={styles.badges}>
      <BadgeItem
        shallShowBadge={!!statusString}
        color="blue__dark"
        dataTestId="vehicle-tile-badge-status"
        label={statusString ?? ''}
      />
      <BadgeItem
        shallShowBadge={isLeasing}
        color="orange"
        dataTestId="vehicle-tile-badge-leasing-offer"
        label={strings.leasingOffer}
      />
      <BadgeItem
        shallShowBadge={isWholesaleSold}
        color="neutral"
        dataTestId="pdp-badge-sold"
        label={strings.soldChipLabel}
      />
    </aside>
  );
};

function getStatusString(
  strings: Record<string, string>,
  vehicleStatus: VehicleStatus | undefined,
) {
  switch (vehicleStatus) {
    case 'inactive':
      return strings.soldChipLabel;
    case 'reserved':
      return strings.reservedChipLabel;
    default:
      return null;
  }
}

export default PdpBadges;
